/**
 * Pieces of the item panel: the item's inputs as a JSON tree, the trace (tool
 * calls, retrieved context) and the values an evaluator's slots read as
 * labelled fields.
 */

import type { ReactNode } from "react";
import { DetailSection as Section } from "@/components/ui/box";
import { JsonView } from "@/components/ui/json-view";
import { inputEntries } from "@/lib/format";

/** Labelled rows; text in full, JSON as a bare tree. */
export function FieldList({ entries, label }: { entries: [string, unknown][]; label: string }) {
  return (
    <dl aria-label={label} className="flex flex-col overflow-hidden rounded-lg border border-line-soft">
      {entries.map(([k, v]) => (
        <div
          key={k}
          className="grid grid-cols-[140px_minmax(0,1fr)] gap-3 border-t border-line-soft bg-well px-3.5 py-2.5 first:border-t-0"
        >
          <dt className="truncate font-mono text-xs font-semibold" title={k}>
            {k}
          </dt>
          <dd className="m-0 min-w-0">
            <JsonView value={v} label={k} toolbar={false} openDepth={1} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** `sourceTraceUrl`: the trace the item was pulled from, when an agent ran on it. */
export function InputSection({
  input,
  sourceTraceUrl,
}: {
  input: Record<string, unknown>;
  sourceTraceUrl?: string | null | undefined;
}) {
  const entries = inputEntries(input ?? {});
  return (
    <Section
      title="Inputs"
      aside={
        sourceTraceUrl && (
          <a className="link text-xs" href={sourceTraceUrl} target="_blank" rel="noreferrer">
            Source trace ↗
          </a>
        )
      }
    >
      {entries.length ? (
        <JsonView value={Object.fromEntries(entries)} label="Inputs" />
      ) : (
        <p className="text-[13px] text-muted">No inputs.</p>
      )}
    </Section>
  );
}

type Call = Record<string, unknown>;
const pick = (c: Call, ...keys: string[]) => keys.map((k) => c[k]).find((v) => v !== undefined);

function toolName(c: Call): string {
  const fn = c.function as Call | undefined;
  return String(pick(c, "name", "tool") ?? fn?.name ?? "tool");
}

function ToolCall({ call, n }: { call: unknown; n: number }) {
  const c: Call = call && typeof call === "object" ? (call as Call) : { result: call };
  const args = pick(c, "args", "arguments", "input") ?? (c.function as Call | undefined)?.arguments;
  const result = pick(c, "result", "output", "response");
  const rows: [string, unknown][] = [];
  if (args !== undefined) rows.push(["args", args]);
  if (result !== undefined) rows.push(["result", result]);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[13px] font-semibold">
        {n}. <span className="font-mono">{toolName(c)}</span>
      </span>
      {rows.length > 0 && <FieldList entries={rows} label={`Tool call ${n}: ${toolName(c)}`} />}
    </div>
  );
}

/** Tool calls and retrieved context, when the connector reported them. */
export function TraceSection({
  toolCalls,
  context,
  aside,
}: {
  toolCalls: unknown[] | null;
  context: string[] | null;
  aside?: ReactNode;
}) {
  if (!toolCalls?.length && !context?.length) return null;
  return (
    <Section title="Trace" aside={aside}>
      <div className="flex flex-col gap-3">
        {/* A read-only list that never reorders, so the position is a stable key. */}
        {toolCalls?.map((c, i) => (
          <ToolCall key={`call-${i}`} call={c} n={i + 1} />
        ))}
        {!!context?.length && (
          <FieldList label="Retrieved context" entries={context.map((c, i) => [`context ${i + 1}`, c])} />
        )}
      </div>
    </Section>
  );
}

/** What one evaluator read, slot by slot. Hidden until the API sends it. */
export function SlotValues({
  slots,
  evaluator,
}: {
  slots: Record<string, unknown> | null | undefined;
  evaluator: string;
}) {
  if (!slots || Object.keys(slots).length === 0) return null;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted">What it read</summary>
      <div className="pt-1.5">
        <FieldList entries={Object.entries(slots)} label={`${evaluator} read`} />
      </div>
    </details>
  );
}
